<template>
    <div
        :style="{
            backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.1) 100%), url('${
                item.image_url || item.image
            }')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            overflow: 'visible',
        }"
        class="my-block ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center"
        @click="$emit('click')"
    >
        <div class="block_header">
            <div class="switch-pastille ppv2-bg-white dark:ppv2-bg-dark" @click.stop>
                <BaseSwitch v-model="localChecked" color="primary" label="" :disabled="!hasAccess" />
            </div>

            <div class="ppv2-relative" @click.stop>
                <button @click="toggleMenu" class="menu-btn ppv2-bg-white dark:ppv2-bg-dark" aria-label="Actions">
                    <Ellipsis class="ppv2-w-7 ppv2-h-7 ppv2-text-black" />
                </button>

                <div
                    v-if="menuVisible"
                    class="menuDropdown ppv2-bg-white dark:ppv2-bg-dark ppv2-absolute ppv2-right-0 overflow-hidden"
                >
                    <button
                        @click="permissionModifyAdditionalSales && $emit('edit', item)"
                        class="menu-item hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-text-black"
                    >
                        <SquarePen class="ppv2-w-4 ppv2-h-4" />
                        <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(14) }">Modifier</span>
                    </button>

                    <button
                        @click="permissionDeleteAdditionalSales && $emit('delete', item)"
                        class="menu-item menu-item-danger hover:!ppv2-text-[#b3261e] hover:ppv2-bg-[#b3261e] hover:ppv2-bg-opacity-10"
                    >
                        <Trash2 class="ppv2-w-4 ppv2-h-4 !ppv2-text-[#b3261e]" />
                        <span class="poppinsLight !ppv2-text-[#b3261e]" :style="{ fontSize: normalizeFontSize(14) }"
                            >Supprimer</span
                        >
                    </button>
                </div>
            </div>
        </div>

        <div
            class="block_footer ppv2-text-white ppv2-flex ppv2-justify-between ppv2-items-center"
            :style="{ padding: normalizeSize(11) }"
        >
            <span class="ppv2-truncate ppv2-max-w-[68%] poppinsMedium" :style="{ fontSize: normalizeFontSize(13) }">{{
                item.name
            }}</span>
            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(13) }">{{ item.price }}{{ $symbolCurrency }}</span>
        </div>
    </div>
</template>

<script>
import BaseSwitch from './BaseSwitch.vue'
import normalizeSize from '../../utils/normalizeSize'
import { Ellipsis, SquarePen, Trash2 } from 'lucide-vue'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'AdditionalSaleCard',
    components: { BaseSwitch, Ellipsis, SquarePen, Trash2 },
    props: {
        item: {
            type: Object,
            required: true,
        },
        productPurchaseOptions: {
            type: Array,
            default: () => [],
        },
        hasAccess: {
            type: Boolean,
            default: false,
        },
        permissionModifyAdditionalSales: {
            type: Boolean,
            default: false,
        },
        permissionDeleteAdditionalSales: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localChecked: this.item.checked !== undefined ? this.item.checked : true,
            menuVisible: false,
        }
    },
    watch: {
        localChecked(newVal) {
            console.log('newVal', newVal)
            this.$emit('update:item', { ...this.item, checked: newVal })
        },

        // sync parent → child
        'item.checked'(newVal) {
            console.log('newVal checked', newVal)
            this.localChecked = newVal
        },

        productPurchaseOptions: {
            handler(newOptions) {
                this.updateCheckedState(newOptions)
            },
            deep: true,
        },
    },
    mounted() {
        this.updateCheckedState(this.productPurchaseOptions)

        // Listen to global event to close menu
        window.addEventListener('close-all-dropdowns', this.closeMenu)

        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        window.removeEventListener('close-all-dropdowns', this.closeMenu)
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        updateCheckedState(options) {
            const itemIds = options.map((opt) => opt.item_id)

            // NEW ITEM → do NOT override default checked
            if (!itemIds.includes(this.item.id)) {
                return
            }

            // EXISTING ITEM → checked only if in options
            this.localChecked = true
        },

        toggleMenu() {
            // If already open → close
            if (this.menuVisible) {
                this.menuVisible = false
                return
            }

            // Close all other dropdowns
            window.dispatchEvent(new CustomEvent('close-all-dropdowns'))

            // Open this dropdown after a tiny delay to avoid closing itself
            setTimeout(() => {
                this.menuVisible = true
            }, 0)
        },

        closeMenu() {
            this.menuVisible = false
        },

        handleClickOutside(event) {
            const menu = this.$el.querySelector('.ppv2-relative')
            if (menu && !menu.contains(event.target)) {
                this.menuVisible = false
            }
        },
    },
}
</script>
<style scoped>
.my-block {
    background-color: #363636;
    overflow: hidden;
    position: relative;
}
.block_header {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 8px;
    z-index: 3;
}
.switch-pastille {
    display: flex;
    align-items: center;
    padding: 2px;
    border-radius: 999px;
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 8px rgba(2, 24, 50, 0.18);
}
.menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    backdrop-filter: blur(8px);
    box-shadow: 0 2px 8px rgba(2, 24, 50, 0.18);
    cursor: pointer;
    border: none;
    padding: 0;
    transition: transform 0.15s ease;
}
.menu-btn:hover {
    transform: scale(1.05);
}
.menuDropdown {
    top: 42px;
    width: 152px;
    padding: 6px;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(2, 24, 50, 0.2);
    z-index: 5;
}
.menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s ease;
}

.menu-item-danger {
    color: #b3261e;
}
.block_footer {
    background: #7d2dbd;
    width: 100%;
    height: 46.55px;
    bottom: 0;
    position: absolute;
    border-radius: 0 0 15px 15px;
}
</style>
